import { useState } from 'react';
import { 
  Box, 
  Button, 
  Card, 
  CardContent, 
  Typography, 
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  IconButton,
  Tooltip,
  Stack,
  Grid,
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  Stop,
  Refresh,
  TrendingUp,
  CheckCircle,
  Error as ErrorIcon,
  Info,
} from '@mui/icons-material';
import {
  useStartFullImport,
  useStartEntityImport,
  usePauseImport,
  useResumeImport,
  useCancelImport,
  useActiveImports,
  useImportJobs,
  useImportSummary,
  useImportProgress,
} from '@/lib/hooks/useSuccessWareImport';
import { ENTITY_TYPES } from '@/lib/services/SuccessWareImportService';

/**
 * Import Management Page
 * 
 * Provides UI for managing SuccessWare imports:
 * - Start full or single entity imports
 * - Monitor active imports with real-time progress
 * - Pause/resume/cancel imports
 * - View import history
 * - Track statistics
 */
export default function ImportManagementPage() {
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [selectedEntity, setSelectedEntity] = useState<string>('all');

  // Mutations
  const startFullImport = useStartFullImport();
  const startEntityImport = useStartEntityImport();
  const pauseImport = usePauseImport();
  const resumeImport = useResumeImport();
  const cancelImport = useCancelImport();

  // Queries
  const { data: activeImports = [] } = useActiveImports(selectedCompany || undefined);
  const { data: allJobs = [] } = useImportJobs(selectedCompany || '');
  const { data: summary } = useImportSummary(selectedCompany || '');

  // Companies (hardcoded for now - could be fetched from sw_companies table)
  const companies = [
    { id: 'hales-ac', name: 'Hales AC' },
    { id: 'climate-control', name: 'Climate Control' },
    { id: 'abraham-ac', name: 'Abraham AC' },
  ];

  const handleStartImport = async () => {
    if (!selectedCompany) {
      alert('Please select a company');
      return;
    }

    try {
      if (selectedEntity === 'all') {
        await startFullImport.mutateAsync({
          company_id: selectedCompany,
          batch_size: 100,
        });
        alert('Full import started! All 10 entity types will be imported.');
      } else {
        await startEntityImport.mutateAsync({
          company_id: selectedCompany,
          entity_type: selectedEntity as any,
          batch_size: 100,
        });
        alert(`Import started for ${selectedEntity}`);
      }
    } catch (error: any) {
      alert(`Failed to start import: ${error.message}`);
    }
  };

  const handlePause = async (jobId: string) => {
    try {
      await pauseImport.mutateAsync(jobId);
    } catch (error: any) {
      alert(`Failed to pause: ${error.message}`);
    }
  };

  const handleResume = async (jobId: string) => {
    try {
      await resumeImport.mutateAsync(jobId);
    } catch (error: any) {
      alert(`Failed to resume: ${error.message}`);
    }
  };

  const handleCancel = async (jobId: string) => {
    if (!confirm('Are you sure you want to cancel this import?')) {
      return;
    }

    try {
      await cancelImport.mutateAsync(jobId);
    } catch (error: any) {
      alert(`Failed to cancel: ${error.message}`);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'running': return 'primary';
      case 'pending': return 'info';
      case 'paused': return 'warning';
      case 'failed': return 'error';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircle fontSize="small" />;
      case 'running': return <TrendingUp fontSize="small" />;
      case 'failed': return <ErrorIcon fontSize="small" />;
      default: return <Info fontSize="small" />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        SuccessWare Import Management
      </Typography>
      
      <Typography variant="body2" color="text.secondary" paragraph>
        Import data from SuccessWare for Hales AC, Climate Control, and Abraham AC.
        Imports are pausable/resumable and process in batches of 100 records.
      </Typography>

      {/* Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4} {...({} as any)}>
              <FormControl fullWidth>
                <InputLabel>Company</InputLabel>
                <Select
                  value={selectedCompany}
                  onChange={(e) => setSelectedCompany(e.target.value)}
                  label="Company"
                >
                  {companies.map(company => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4} {...({} as any)}>
              <FormControl fullWidth>
                <InputLabel>Entity Type</InputLabel>
                <Select
                  value={selectedEntity}
                  onChange={(e) => setSelectedEntity(e.target.value)}
                  label="Entity Type"
                >
                  <MenuItem value="all">All Entities (10 types)</MenuItem>
                  {ENTITY_TYPES.map(entity => (
                    <MenuItem key={entity} value={entity}>
                      {entity.replace('_', ' ').toUpperCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4} {...({} as any)}>
              <Button
                variant="contained"
                startIcon={<PlayArrow />}
                onClick={handleStartImport}
                disabled={!selectedCompany || startFullImport.isPending || startEntityImport.isPending}
                fullWidth
                size="large"
              >
                Start Import
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      {summary && selectedCompany && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3} {...({} as any)}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Jobs
                </Typography>
                <Typography variant="h4">
                  {summary.total_jobs}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3} {...({} as any)}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Active Jobs
                </Typography>
                <Typography variant="h4" color="primary">
                  {summary.active_jobs}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3} {...({} as any)}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Records Imported
                </Typography>
                <Typography variant="h4" color="success.main">
                  {summary.total_records_imported.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3} {...({} as any)}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Records Failed
                </Typography>
                <Typography variant="h4" color="error.main">
                  {summary.total_records_failed.toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Active Imports */}
      {activeImports.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Active Imports
            </Typography>

            {activeImports.map(job => (
              <ActiveImportCard
                key={job.id}
                job={job}
                onPause={handlePause}
                onResume={handleResume}
                onCancel={handleCancel}
              />
            ))}
          </CardContent>
        </Card>
      )}

      {/* Import History */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Import History
          </Typography>

          {allJobs.length === 0 ? (
            <Alert severity="info">
              No imports yet. Select a company and click "Start Import" to begin.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Entity Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Progress</TableCell>
                    <TableCell align="right">Imported</TableCell>
                    <TableCell align="right">Failed</TableCell>
                    <TableCell>Started</TableCell>
                    <TableCell>Completed</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allJobs.map(job => (
                    <TableRow key={job.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {job.job_type.replace('_', ' ')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(job.status)}
                          label={job.status}
                          color={getStatusColor(job.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        {job.total_pages ? (
                          <Typography variant="body2">
                            {job.current_page} / {job.total_pages}
                          </Typography>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="success.main">
                          {job.records_imported.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="error.main">
                          {job.records_failed.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {job.started_at ? new Date(job.started_at).toLocaleString() : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {job.completed_at ? new Date(job.completed_at).toLocaleString() : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {job.status === 'running' && (
                          <Tooltip title="Pause">
                            <IconButton size="small" onClick={() => handlePause(job.id)}>
                              <Pause fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {job.status === 'paused' && (
                          <Tooltip title="Resume">
                            <IconButton size="small" onClick={() => handleResume(job.id)}>
                              <Refresh fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(job.status === 'running' || job.status === 'paused' || job.status === 'pending') && (
                          <Tooltip title="Cancel">
                            <IconButton size="small" onClick={() => handleCancel(job.id)} color="error">
                              <Stop fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

/**
 * Active Import Card Component
 * Shows detailed progress for an active import job
 */
function ActiveImportCard({
  job,
  onPause,
  onResume,
  onCancel,
}: {
  job: any;
  onPause: (id: string) => void;
  onResume: (id: string) => void;
  onCancel: (id: string) => void;
}) {
  const { data: progress } = useImportProgress(job.id);

  if (!progress) return null;

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Stack spacing={2}>
          {/* Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
              {job.job_type.replace('_', ' ')}
            </Typography>
            
            <Stack direction="row" spacing={1}>
              {job.status === 'running' && (
                <Button
                  size="small"
                  startIcon={<Pause />}
                  onClick={() => onPause(job.id)}
                  variant="outlined"
                >
                  Pause
                </Button>
              )}
              {job.status === 'paused' && (
                <Button
                  size="small"
                  startIcon={<Refresh />}
                  onClick={() => onResume(job.id)}
                  variant="contained"
                >
                  Resume
                </Button>
              )}
              <Button
                size="small"
                startIcon={<Stop />}
                onClick={() => onCancel(job.id)}
                color="error"
                variant="outlined"
              >
                Cancel
              </Button>
            </Stack>
          </Box>

          {/* Progress Bar */}
          <Box>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2" color="text.secondary">
                Progress: {progress.percentComplete}%
              </Typography>
              {progress.estimatedTimeRemaining && (
                <Typography variant="body2" color="text.secondary">
                  ETA: {progress.estimatedTimeRemaining}
                </Typography>
              )}
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress.percentComplete}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>

          {/* Stats */}
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3} {...({} as any)}>
              <Typography variant="body2" color="text.secondary">
                Page
              </Typography>
              <Typography variant="h6">
                {job.current_page} / {job.total_pages || '?'}
              </Typography>
            </Grid>

            <Grid item xs={6} sm={3} {...({} as any)}>
              <Typography variant="body2" color="text.secondary">
                Imported
              </Typography>
              <Typography variant="h6" color="success.main">
                {job.records_imported.toLocaleString()}
              </Typography>
            </Grid>

            <Grid item xs={6} sm={3} {...({} as any)}>
              <Typography variant="body2" color="text.secondary">
                Failed
              </Typography>
              <Typography variant="h6" color="error.main">
                {job.records_failed.toLocaleString()}
              </Typography>
            </Grid>

            <Grid item xs={6} sm={3} {...({} as any)}>
              <Typography variant="body2" color="text.secondary">
                Batches
              </Typography>
              <Typography variant="h6">
                {progress.batchStats.completed_batches} / {progress.batchStats.total_batches}
              </Typography>
            </Grid>
          </Grid>
        </Stack>
      </CardContent>
    </Card>
  );
}
